import { useState } from 'react';
import kalp2Icon from '@/assets/kalp2.png';
import tartiIcon from '@/assets/tarti.png';
import enjektorIcon from '@/assets/enjektor.png';
import { Strings } from '@/constants/strings';
import { ToolInfoModal } from '@/components/ToolInfoModal';
import type { UserMode } from '@/components/RiskCalculator';

interface SelectionScreenProps {
  userMode: UserMode;
  onSelectRisk: () => void;
  onSelectBmi: () => void;
  onSelectFindRisk: () => void;
}

const CARD_BASE =
  'group relative flex w-full items-center overflow-hidden rounded-3xl border border-slate-100 bg-card-bg p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg';

export function SelectionScreen({
  userMode,
  onSelectRisk,
  onSelectBmi,
  onSelectFindRisk,
}: SelectionScreenProps) {
  const [score2InfoOpen, setScore2InfoOpen] = useState(false);
  const [bmiInfoOpen, setBmiInfoOpen] = useState(false);
  const [findRiskInfoOpen, setFindRiskInfoOpen] = useState(false);
  const isPublic = userMode === 'public';

  const INFO_BUTTON_CLASS =
    'flex size-14 shrink-0 items-center justify-center self-center rounded-2xl border-2 text-base font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2';

  return (
    <div className="flex min-h-screen-safe flex-col bg-surface px-6 pb-10 pt-8 sm:pt-12">
      <p className="mb-8 text-center text-base text-text-muted">
        Bir araç seçin
      </p>

      <div className="flex flex-col space-y-6">
        {/* SCORE2 Card */}
        <div className="flex w-full items-stretch gap-3">
          <button
            type="button"
            onClick={onSelectRisk}
            className={`${CARD_BASE} min-w-0 flex-1 text-left`}
          >
            <img
              src={kalp2Icon}
              alt=""
              className="absolute -right-2 top-1/2 h-24 w-24 shrink-0 -translate-y-1/2 object-contain"
              aria-hidden
            />
            <div className="relative flex min-w-0 flex-1 items-center pr-28 sm:pr-0">
              <span className="block font-bold text-text-heading text-sm sm:text-lg">
                <span>SCORE2 - </span>
                <span>Kardiyovasküler Risk</span>
              </span>
            </div>
          </button>
          {isPublic && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setScore2InfoOpen(true);
              }}
              className={`${INFO_BUTTON_CLASS} border-[#87ceeb] bg-[#87ceeb]/15 text-[#87ceeb] hover:bg-[#87ceeb]/25 focus:ring-[#87ceeb]`}
              aria-label="SCORE2 nedir?"
            >
              ?
            </button>
          )}
        </div>

        {/* BMI Card */}
        <div className="flex w-full items-stretch gap-3">
          <button
            type="button"
            onClick={onSelectBmi}
            className={`${CARD_BASE} min-w-0 flex-1 text-left`}
          >
            <img
              src={tartiIcon}
              alt=""
              className="absolute right-4 top-1/2 h-10 w-10 shrink-0 -translate-y-1/2 object-contain"
              aria-hidden
            />
            <div className="relative flex min-w-0 flex-1 items-center pr-10 sm:pr-0">
              <span className="block text-sm font-bold text-text-heading sm:text-lg">
                {Strings.bmiButton}
              </span>
            </div>
          </button>
          {isPublic && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setBmiInfoOpen(true);
              }}
              className={`${INFO_BUTTON_CLASS} border-[#87ceeb] bg-[#87ceeb]/15 text-[#87ceeb] hover:bg-[#87ceeb]/25 focus:ring-[#87ceeb]`}
              aria-label="BMI nedir?"
            >
              ?
            </button>
          )}
        </div>

        {/* FindRisk Card */}
        <div className="flex w-full items-stretch gap-3">
          <button
            type="button"
            onClick={onSelectFindRisk}
            className={`${CARD_BASE} min-w-0 flex-1 text-left`}
          >
            <img
              src={enjektorIcon}
              alt=""
              className="absolute right-4 top-1/2 h-10 w-10 shrink-0 -translate-y-1/2 object-contain"
              aria-hidden
            />
            <div className="relative flex min-w-0 flex-1 items-center pr-10 sm:pr-0">
              <span className="block text-sm font-bold text-text-heading sm:text-lg">
                {Strings.findRiskButton}
              </span>
            </div>
          </button>
          {isPublic && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFindRiskInfoOpen(true);
              }}
              className={`${INFO_BUTTON_CLASS} border-[#87ceeb] bg-[#87ceeb]/15 text-[#87ceeb] hover:bg-[#87ceeb]/25 focus:ring-[#87ceeb]`}
              aria-label="FindRisk nedir?"
            >
              ?
            </button>
          )}
        </div>
      </div>

      <ToolInfoModal
        visible={score2InfoOpen}
        title={Strings.algorithmModalScore2Heading}
        body={Strings.algorithmModalScore2Text}
        onClose={() => setScore2InfoOpen(false)}
      />
      <ToolInfoModal
        visible={bmiInfoOpen}
        title={Strings.bmiInfoTitle}
        body={Strings.bmiInfoBody}
        onClose={() => setBmiInfoOpen(false)}
      />
      <ToolInfoModal
        visible={findRiskInfoOpen}
        title={Strings.findRiskInfoTitle}
        body={Strings.findRiskInfoBody}
        onClose={() => setFindRiskInfoOpen(false)}
      />
    </div>
  );
}
